import { useState, useCallback } from 'react';

import sotApi from 'api/sotApi';
import {
  formatSubheadRefId,
  getNextSubheadRefSequence,
  getSubheadQueryResults,
} from './subheadUtils';

const emptyRow = () => ({
  clientId: `new-${Date.now()}-${Math.random()}`,
  aooh_subhead_id: null,
  refid: null,
  subhead_text: '',
});

export const useSubheads = () => {
  const [subheads, setSubheads] = useState([]);
  const [originalSubheads, setOriginalSubheads] = useState([]);
  const [loadedHeadlineRefid, setLoadedHeadlineRefid] = useState(null);

  const loadSubheads = useCallback(async (headlineRefid) => {
    if (!headlineRefid) {
      setSubheads([]);
      setOriginalSubheads([]);
      setLoadedHeadlineRefid(null);
      return;
    }
    const res = await sotApi.aoohSubheads.getByHeadlineRefid(headlineRefid);
    const rows = getSubheadQueryResults(res).map((item) => ({
      clientId: String(item.aooh_subhead_id),
      aooh_subhead_id: item.aooh_subhead_id,
      refid: item.refid,
      subhead_text: item.subhead_text || '',
    }));
    setSubheads(rows);
    setOriginalSubheads(rows.map((r) => ({ ...r })));
    setLoadedHeadlineRefid(headlineRefid);
  }, []);

  const resetSubheadsForClone = useCallback((rows) => {
    const cloned = (rows || []).map((item) => ({
      ...emptyRow(),
      subhead_text: item.subhead_text || '',
    }));
    setSubheads(cloned);
    setOriginalSubheads([]);
    setLoadedHeadlineRefid(null);
  }, []);

  const addSubhead = useCallback(() => {
    setSubheads((prev) => [...prev, emptyRow()]);
  }, []);

  const removeSubhead = useCallback((clientId) => {
    setSubheads((prev) => prev.filter((row) => row.clientId !== clientId));
  }, []);

  const updateSubheadText = useCallback((clientId, text) => {
    setSubheads((prev) =>
      prev.map((row) =>
        row.clientId === clientId ? { ...row, subhead_text: text } : row
      )
    );
  }, []);

  const syncSubheads = useCallback(
    async (headlineRefid, previousHeadlineRefid) => {
      const parentRefid = headlineRefid?.trim()?.slice(0, 7);
      if (!parentRefid) {
        return;
      }

      const existingRefids = [
        ...originalSubheads.map((s) => s.refid).filter(Boolean),
        ...subheads.map((s) => s.refid).filter(Boolean),
      ];
      let nextSeq = getNextSubheadRefSequence(existingRefids);

      const originalById = new Map(
        originalSubheads.map((s) => [s.aooh_subhead_id, s])
      );
      const currentIds = new Set(
        subheads.filter((s) => s.aooh_subhead_id).map((s) => s.aooh_subhead_id)
      );

      const deletes = originalSubheads.filter(
        (s) => s.aooh_subhead_id && !currentIds.has(s.aooh_subhead_id)
      );
      await Promise.all(
        deletes.map((s) => sotApi.aoohSubheads.delete(s.aooh_subhead_id))
      );

      for (let index = 0; index < subheads.length; index += 1) {
        const row = subheads[index];
        const displayorder = index + 1;
        const subheadText = row.subhead_text ?? '';

        if (row.aooh_subhead_id) {
          if (!subheadText.trim()) {
            await sotApi.aoohSubheads.delete(row.aooh_subhead_id);
            continue;
          }
          await sotApi.aoohSubheads.update(row.aooh_subhead_id, {
            headline_refid: parentRefid,
            subhead_text: subheadText,
            displayorder,
          });
        } else if (subheadText.trim()) {
          const refid = formatSubheadRefId(nextSeq);
          nextSeq += 1;
          await sotApi.aoohSubheads.add({
            refid,
            headline_refid: parentRefid,
            displayorder,
            subhead_text: subheadText,
          });
        }
      }
    },
    [subheads, originalSubheads]
  );

  return {
    subheads,
    loadedHeadlineRefid,
    loadSubheads,
    resetSubheadsForClone,
    addSubhead,
    removeSubhead,
    updateSubheadText,
    syncSubheads,
    setSubheads,
  };
};

export default useSubheads;
